import { roundCurrency } from '@bill-alps/backend/shared/finance/money';

export interface VatBucketItem {
  vatRate: number;
  quantity: number;
  unitPrice: number;
}

export interface VatBucketBill {
  discount?: number;
  shippingCosts?: number;
  items: VatBucketItem[];
}

/**
 * VAT-exclusive base per rate of an invoice, with the header-level shipping
 * and discount allocated pro rata across the rate buckets (art. 24 al. 1
 * LTVA: the consideration includes ancillary costs and is net of
 * discounts). The rounding residual lands on the largest bucket so the
 * bucket sum equals `subtotal + shipping − discount` to the centime.
 *
 * Shared by the invoice totals (`calculateBillTotals` v2), the VAT
 * declaration and the ledger's revenue/VAT lines, so the three can never
 * disagree.
 */
export function billVatBuckets(bill: VatBucketBill): Map<number, number> {
  const buckets = new Map<number, number>();
  let subtotal = 0;
  for (const item of bill.items) {
    const lineTotal = roundCurrency(item.quantity * item.unitPrice);
    subtotal += lineTotal;
    buckets.set(item.vatRate, (buckets.get(item.vatRate) ?? 0) + lineTotal);
  }
  subtotal = roundCurrency(subtotal);

  const adj = roundCurrency((bill.shippingCosts ?? 0) - (bill.discount ?? 0));
  if (adj === 0) return buckets;
  if (subtotal === 0) {
    buckets.set(0, roundCurrency((buckets.get(0) ?? 0) + adj));
    return buckets;
  }

  const factor = 1 + adj / subtotal;
  let allocated = 0;
  let largestRate = 0;
  let largestBase = -Infinity;
  for (const [rate, base] of buckets) {
    const adjusted = roundCurrency(base * factor);
    buckets.set(rate, adjusted);
    allocated += adjusted;
    if (base > largestBase) {
      largestBase = base;
      largestRate = rate;
    }
  }
  const residual = roundCurrency(subtotal + adj) - roundCurrency(allocated);
  if (residual !== 0) {
    buckets.set(
      largestRate,
      roundCurrency((buckets.get(largestRate) ?? 0) + residual),
    );
  }
  return buckets;
}

/** Σ of `base × rate` over the buckets, each rounded to the centime. */
export function vatFromBuckets(buckets: Map<number, number>): number {
  let vat = 0;
  for (const [rate, base] of buckets) {
    vat += roundCurrency((base * rate) / 100);
  }
  return roundCurrency(vat);
}

export interface VatBreakdownRow {
  rate: number;
  /** VAT-exclusive base taxed at this rate. */
  base: number;
  vat: number;
}

export interface VatBreakdownDocument extends VatBucketBill {
  /** The version the stored VAT was derived with; absent = 1. */
  totalsVersion?: number;
  /** The VAT printed on the document: the rows add up to exactly this. */
  vatAmount: number;
}

/**
 * The VAT printed on an invoice or a quote, detailed per rate, highest rate
 * first. The stored `vatAmount` stays authoritative (art. 27 al. 2 LTVA): the
 * totals can round the sum of the lines where this rounds each rate, so a
 * rounding gap lands on the largest rate, the same correction as
 * `vatLinesForBill`.
 *
 * Rates with a zero base are left out. Empty when no rate is left, or when the
 * lines cannot explain the stored VAT (rows old enough to carry it in the
 * header `vatRate`, lines at 0): callers then print the amount alone instead
 * of inventing rates.
 */
export function documentVatBreakdown(
  doc: VatBreakdownDocument,
): VatBreakdownRow[] {
  // The stored version, not `billTotalsVersion()`: that one says how to
  // recompute a draft, this one how the VAT being printed was derived.
  const buckets = billVatBuckets(
    doc.totalsVersion === 2 ? doc : { items: doc.items },
  );
  const rows: VatBreakdownRow[] = [];
  for (const [rate, unroundedBase] of buckets) {
    const base = roundCurrency(unroundedBase);
    if (base === 0) continue;
    rows.push({ rate, base, vat: roundCurrency((base * rate) / 100) });
  }

  // Rounding moves the sum by at most a centime per taxed rate.
  const taxed = rows.filter((row) => row.rate !== 0);
  const gap = roundCurrency(
    doc.vatAmount - rows.reduce((sum, row) => sum + row.vat, 0),
  );
  const explainedByRounding = Math.round(Math.abs(gap) * 100) <= taxed.length;
  if (!explainedByRounding) return [];
  if (gap !== 0) {
    const largest = taxed.reduce((a, b) => (b.base > a.base ? b : a));
    largest.vat = roundCurrency(largest.vat + gap);
  }

  return rows.sort((a, b) => b.rate - a.rate);
}
